import { InfoCircleOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import EmptyWarning from '@zeus/core/EmptyWarning';
import JqExpressionField from '@zeus/core/settings/JqExpressionField';
import { templateVariables, FLOW_CONFIGURATION_S3_EXTENSIONS } from '_/constants/common';
import { IFlowDefinitionChain } from '_/features/flows/common/types/flow.types';
import { chunkInputProps } from '_/features/flows/common/utils/flowUtils';
import { FlowValues } from '_/features/flows/flowPage/components/NewProjectFlow';
import { ICollection } from '_/types/collections/collections.types';
import { Card, Divider, Switch, Input, Form, Button, Popover, Select, InputNumber, Checkbox, Space, Alert } from 'antd';
import React, { Fragment, useState, useEffect, ChangeEvent } from 'react';

import FlowDefinition from '../FlowDefinition';

import classes from './SimpleExtractorFlow.module.css';

const extensionOptions = FLOW_CONFIGURATION_S3_EXTENSIONS.map((ext) => ({
  label: ext,
  value: ext,
  key: ext,
}));

const defaultS3Vals = {
  inputUriTemplate: {
    bucket: '',
    path: '',
    fileName: '',
    extension: '.json',
  },
  chunks: '',
  chunkCollectHours: '',
  chunkPushHours: '',
  encryptedConfig: {
    accessKeyId: '',
    secretAccessKey: '',
  },
};

const initialFilterValues = { filters: [{ parameters: {} }] };

interface SimpleExtractorFlowProps {
  addChainToFlowArray: () => void;
  chained: boolean;
  collections: ICollection[];
  flow: FlowValues;
  handleChainCancel: (e: React.MouseEvent<HTMLElement>, idx: number) => void;
  handleChainChange: (event: ChangeEvent<HTMLInputElement> | { target: { name: string; value: any } }, idx: number) => void;
  handleFieldChange: (
    event: ChangeEvent<HTMLInputElement> | { target: { name: string; value?: any; checked?: boolean; type?: string } },
  ) => void;
  handleS3Change: (event: ChangeEvent<HTMLInputElement> | { target: { name: string; value: any } }) => void;
  handleSourceFilterChange: (event: ChangeEvent<HTMLInputElement>, idx: number) => void;
  invalidChainArray: number[];
  isEditMode: boolean;
  isFormDirty: boolean;
  resetS3Values: (s3Vals: any) => void;
  s3ConfiguredBucket: string;
  orgSlug: string;
  projectSlug: string;
}

function SimpleExtractorFlow({
  addChainToFlowArray,
  chained,
  collections,
  flow,
  handleChainCancel,
  handleChainChange,
  handleFieldChange,
  handleS3Change,
  handleSourceFilterChange,
  invalidChainArray,
  isEditMode,
  isFormDirty,
  resetS3Values,
  s3ConfiguredBucket,
  orgSlug,
  projectSlug,
}: SimpleExtractorFlowProps) {
  const [openS3Popover, setOpenS3Popover] = useState(false);
  const [s3BucketDisplay, setS3BucketDisplay] = useState('');
  const [showS3Fields, setShowS3Fields] = useState(false);
  const [chains, setChains] = useState<IFlowDefinitionChain[]>([]);

  // FlowSourceFilters - inlined state
  const [showSourceFiltering, setShowSourceFiltering] = useState(false);
  const [flowCollectionParameters, setFlowCollectionParameters] = useState<string[] | null>(null);
  const [flowSourceCriteria, setFlowSourceCriteria] = useState(initialFilterValues);

  useEffect(() => {
    if (s3ConfiguredBucket) {
      setS3BucketDisplay(s3ConfiguredBucket);
    } else if (!s3ConfiguredBucket && isFormDirty) {
      setS3BucketDisplay('');
    }
  }, [s3ConfiguredBucket, isFormDirty]);

  useEffect(() => {
    if (!flow.s3Configuration) {
      resetS3Values(defaultS3Vals);
    }
  }, [flow.s3Configuration, resetS3Values]);

  useEffect(() => {
    if (chained) {
      setChains(flow?.chains || []);
      setShowS3Fields(true);
    } else if (!chained && !s3ConfiguredBucket) {
      setShowS3Fields(false);
    } else if (!chained && s3ConfiguredBucket) {
      setShowS3Fields(true);
    }
  }, [chained, s3ConfiguredBucket, flow.chains]);

  // FlowSourceFilters - inlined useEffect hooks
  useEffect(() => {
    setShowSourceFiltering(flow.sourceFiltering);
  }, [flow.sourceFiltering]);

  useEffect(() => {
    const collection = collections.find((coll) => coll.id === flow.collectionId);
    if (collection) {
      setFlowCollectionParameters(collection?.parameters);
    }
  }, [flow.collectionId, collections]);

  useEffect(() => {
    if (flow.sourceCriteria) {
      setFlowSourceCriteria({ ...flow.sourceCriteria });
    } else {
      setFlowSourceCriteria(initialFilterValues);
    }
  }, [flow.sourceCriteria]);

  const handleS3Popover = () => {
    setOpenS3Popover(true);
  };

  const closeS3Popover = () => {
    setOpenS3Popover(false);
  };

  const toggleS3ConfigurationFields = (checked) => {
    handleFieldChange({
      target: { name: 's3Configuration', checked: checked, type: 'checkbox' },
    } as any);
    setShowS3Fields(checked);
  };

  // FlowSourceFilters - inlined functions
  const toggleSourceFiltering = (checked) => {
    handleFieldChange({
      target: { name: 'sourceFiltering', checked: checked, type: 'checkbox' },
    } as any);
    setShowSourceFiltering(checked);
  };

  const setParameterFilterValues = (event, index) => {
    event.persist();
    handleSourceFilterChange(event, index);
  };

  const s3Checked = flow.s3Configuration || (chained && showS3Fields);

  return (
    <Fragment>
      {/* configurable amount of time before the start of the delivery */}
      <div className={`${classes.formContainer} ${classes.marginTop}`}>
        {!chained && <FlowDefinition collections={collections} handleFieldChange={handleFieldChange} isEditMode={isEditMode} />}
        {chained && (
          <div>
            <Divider className={classes.divider} />
            <h2>Chained Extractor</h2>
            {chains?.length ? (
              chains.map((chain, idx) => {
                const newCollectionUrl = `/orgs/${orgSlug}/projects/${projectSlug}/collections/_new`;
                const href = (
                  <a href={newCollectionUrl} className={classes.warningUrl}>
                    collection
                  </a>
                );
                const segment = idx + 1;

                const collectionOptions = collections.map((collection) => ({
                  label: collection.name,
                  value: collection.id,
                  key: collection.name,
                }));

                return (
                  <Fragment key={`chain-${idx}`}>
                    {collections?.length ? (
                      <Card className={classes.chainCard} data-test-id={`chain-segment-${segment}`}>
                        <div className={classes.h3Container}>
                          <h3 className={classes.marginBottom}>Segment {segment}:</h3>
                          <Button
                            type="text"
                            shape="circle"
                            onClick={(e) => handleChainCancel(e, idx)}
                            icon={<CloseOutlined />}
                          />
                        </div>
                        {invalidChainArray.length > 0 && invalidChainArray.indexOf(idx) >= 0 && isFormDirty && (
                          <Alert
                            key={idx}
                            message={'Needs both a Collection and a JQ Transform to be valid'}
                            type="error"
                            showIcon
                            style={{ marginBottom: 10 }}
                          />
                        )}
                        {idx !== 0 && (
                          <Form.Item
                            label="JQ Input Transform"
                            name={`chain-${idx}-transform`}
                            rules={[{ required: true, message: 'JQ Input Transform is required' }]}
                            className={classes.marginBottom10}
                          >
                            {/* No history yet -- this flow doesn't have an id until it's created. */}
                            <JqExpressionField
                              id={`transform-${idx}`}
                              rows={4}
                              onChange={(value) => handleChainChange({ target: { name: 'transform', value: value } } as any, idx)}
                              value={chain.transform || ''}
                              inputProps={{ 'name': 'transform', 'aria-describedby': `simpleflow-chain-jqTransform-${idx}` }}
                            />
                          </Form.Item>
                        )}
                        {idx !== 0 && (
                          <Form.Item
                            label="JQ Deduplication Transform"
                            name={`chain-${idx}-keyTransform`}
                            className={classes.marginBottom10}
                          >
                            <JqExpressionField
                              id={`dedupe-transform-${idx}`}
                              rows={4}
                              onChange={(value) =>
                                handleChainChange({ target: { name: 'keyTransform', value: value } } as any, idx)
                              }
                              value={chain.keyTransform || ''}
                              inputProps={{ 'name': 'keyTransform', 'aria-describedby': `simpleflow-chain-keyTransform-${idx}` }}
                            />
                          </Form.Item>
                        )}
                        <Form.Item
                          label="Collection"
                          name={`chain-${idx}-collectionId`}
                          rules={[{ required: true, message: 'Collection is required' }]}
                          className={classes.marginBottom10}
                        >
                          <Select
                            id={`simpleflow-chain-collection-${idx}`}
                            placeholder="Select a collection"
                            options={collectionOptions}
                            onChange={(value) =>
                              handleChainChange({ target: { name: 'collectionId', value: value } } as any, idx)
                            }
                            value={chain.collectionId}
                          />
                        </Form.Item>
                        <Form.Item
                          label="Hours to Collect Data"
                          name={`chain-${idx}-dataHours`}
                          rules={[
                            {
                              type: 'number',
                              min: 0.01,
                              max: 999,
                              message: 'Must be between 0.01 and 999',
                            },
                          ]}
                          className={classes.marginBottom10}
                        >
                          <InputNumber
                            id={`segmentDataHours-${idx}`}
                            name="dataHours"
                            aria-describedby={`simpleflow-chain-dataHours-${idx}`}
                            onChange={(value) => handleChainChange({ target: { name: 'dataHours', value: value } } as any, idx)}
                            value={chain.dataHours}
                            className={classes.fullWidth}
                            min={0.01}
                            max={999}
                            step={0.01}
                          />
                        </Form.Item>
                        <Form.Item
                          label="Chunk Collect Hours"
                          name={`chain-${idx}-chunkCollectHours`}
                          rules={[
                            {
                              type: 'number',
                              min: chunkInputProps.min,
                              max: chunkInputProps.max,
                              message: `Must be between ${chunkInputProps.min} and ${chunkInputProps.max}`,
                            },
                          ]}
                          className={classes.marginBottom10}
                        >
                          <InputNumber
                            id={`segmentChunkCollectHours-${idx}`}
                            name="chunkCollectHours"
                            aria-describedby={`simpleflow-chain-chunk-collect-hours-${idx}`}
                            onChange={(value) =>
                              handleChainChange({ target: { name: 'chunkCollectHours', value: value } } as any, idx)
                            }
                            value={chain.chunkCollectHours}
                            className={classes.fullWidth}
                            min={chunkInputProps.min}
                            max={chunkInputProps.max}
                            step={chunkInputProps.step}
                          />
                        </Form.Item>
                      </Card>
                    ) : (
                      <EmptyWarning message={<span>Please create a new {href} before finishing this flow</span>} />
                    )}
                  </Fragment>
                );
              })
            ) : (
              <EmptyWarning message="You must Add a chain or change the Flow type." />
            )}
            <Button
              type="default"
              icon={<PlusOutlined />}
              block
              style={{ marginTop: 20 }}
              disabled={chains.length === 10}
              onClick={addChainToFlowArray}
            >
              Add Another Chain
            </Button>
          </div>
        )}
        <Divider className={classes.divider} />
        {/* FlowSourceFilters - inlined */}
        <div data-test-id="source-filtering">
          <Space>
            <Switch checked={flow.sourceFiltering} onChange={toggleSourceFiltering} disabled={!flow.collectionId} />
            <span>Do you want to filter the sources included in this flow?</span>
          </Space>
          {showSourceFiltering && (
            <Card className={classes.cardMarginTop} styles={{ body: { padding: '10px' } }}>
              <Space direction="vertical" className={classes.fullWidth}>
                <div className={classes.sectionTitle}>
                  Source Filters (leave field blank if filter is not needed on a specific parameter):
                </div>
                <div>
                  {flowSourceCriteria?.filters?.map((filter, idx) => (
                    <Fragment key={idx}>
                      {flowCollectionParameters?.map((param) => (
                        <Form.Item
                          key={param}
                          label={param}
                          name={`filter-${idx}-${param}`}
                          rules={[
                            {
                              whitespace: true,
                              message: 'Cannot contain only whitespace',
                            },
                            {
                              max: 500,
                              message: 'Filter value cannot exceed 500 characters',
                            },
                          ]}
                        >
                          <Input
                            name={param}
                            id={`source-filter-${param}`}
                            placeholder={`Enter ${param} filter value`}
                            value={filter.parameters[param] || ''}
                            onChange={(e) => setParameterFilterValues(e, idx)}
                          />
                        </Form.Item>
                      ))}
                    </Fragment>
                  ))}
                </div>
              </Space>
            </Card>
          )}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.switchContainer}>
          <Switch
            checked={flow.skipQaTests}
            onChange={(checked) =>
              handleFieldChange({
                target: { name: 'skipQaTests', checked: checked, type: 'checkbox' },
              } as any)
            }
            data-test-id="skip-qa-tests-toggle"
          />
          <span>Do you want to skip QA checks for this flow?</span>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.switchContainer}>
          <Switch
            checked={s3Checked || chained}
            disabled={chained}
            onChange={toggleS3ConfigurationFields}
            data-test-id="flow-s3-config-toggle"
          />
          <span>Do you want to add an S3 configuration (and chunks)?</span>
        </div>
        {showS3Fields && (
          <Card className={classes.s3Card} styles={{ body: { padding: '10px' } }}>
            {/* FlowS3Configuration - inlined */}
            <div>
              <div className={classes.sectionTitle}>Configuration: {chained ? '(Required for Chained Flows)' : null}</div>
              <Form.Item label="S3 Configuration">
                <Space>
                  <Space.Compact>
                    <Input value="s3://" disabled style={{ width: 60 }} />
                    <Form.Item
                      name="bucket"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: 'Bucket is required',
                        },
                        {
                          whitespace: true,
                          message: 'Cannot contain only whitespace',
                        },
                        {
                          max: 255,
                          message: 'Bucket name cannot exceed 255 characters',
                        },
                      ]}
                    >
                      <Input
                        id="bucket"
                        placeholder="Bucket"
                        aria-describedby="S3-bucket"
                        onChange={handleS3Change}
                        style={{ width: 150 }}
                      />
                    </Form.Item>
                    <Input value="/" disabled style={{ width: 30 }} />
                    <Form.Item
                      name="path"
                      noStyle
                      rules={[
                        {
                          whitespace: true,
                          message: 'Cannot contain only whitespace',
                        },
                        {
                          max: 500,
                          message: 'Path cannot exceed 500 characters',
                        },
                      ]}
                    >
                      <Input
                        id="template"
                        placeholder="Path Template"
                        aria-describedby="S3-template"
                        onChange={handleS3Change}
                        style={{ width: isEditMode ? 300 : 200 }}
                      />
                    </Form.Item>
                    {!isEditMode && (
                      <>
                        <Input value="/" disabled style={{ width: 30 }} />
                        <Form.Item
                          name="fileName"
                          noStyle
                          rules={[
                            {
                              whitespace: true,
                              message: 'Cannot contain only whitespace',
                            },
                            {
                              max: 255,
                              message: 'Filename cannot exceed 255 characters',
                            },
                          ]}
                        >
                          <Input
                            id="filenameTemplate"
                            placeholder="Filename"
                            aria-describedby="S3-fileNameTemplate"
                            onChange={handleS3Change}
                            style={{ width: 150 }}
                          />
                        </Form.Item>
                      </>
                    )}
                    <Form.Item name="extension" noStyle>
                      <Select
                        onChange={(value) => handleS3Change({ target: { name: 'extension', value: value } } as any)}
                        options={extensionOptions}
                        style={{ width: 100 }}
                      />
                    </Form.Item>
                  </Space.Compact>
                  <Popover
                    open={openS3Popover}
                    onOpenChange={(visible) => {
                      if (!visible) closeS3Popover();
                    }}
                    content={<div className={classes.typography}>{templateVariables('flow')}</div>}
                    placement="right"
                  >
                    <Button
                      type="text"
                      shape="circle"
                      onClick={handleS3Popover}
                      icon={<InfoCircleOutlined className={classes.iconBlue} />}
                    />
                  </Popover>
                </Space>
              </Form.Item>
              {s3BucketDisplay && (
                <Alert
                  type="success"
                  className={classes.marginBottom20}
                  description={
                    <div>
                      <span className={classes.boldText}>Preview:</span> {s3BucketDisplay}
                    </div>
                  }
                />
              )}
            </div>
            {s3Checked && (
              <Form.Item
                name="inputTransform"
                label="Do you want to specify an input transform?"
                rules={[
                  {
                    whitespace: true,
                    message: 'Cannot contain only whitespace',
                  },
                  {
                    max: 1000,
                    message: 'Input transform cannot exceed 1000 characters',
                  },
                ]}
              >
                <Input
                  placeholder="JQ Transform"
                  id="inputTransform"
                  aria-describedby="simpleflow-inputTransform"
                  onChange={handleFieldChange}
                />
              </Form.Item>
            )}
            <div>
              <div className={classes.sectionTitle}>If either of these values are given, both are overwritten:</div>
              <Form.Item
                name="accessKeyId"
                label="Access Key Id"
                rules={[
                  {
                    whitespace: true,
                    message: 'Cannot contain only whitespace',
                  },
                ]}
              >
                <Input.Password id="accessKeyId" aria-describedby="access-key-id" autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="secretAccessKey"
                label="Secret Access Key"
                rules={[
                  {
                    whitespace: true,
                    message: 'Cannot contain only whitespace',
                  },
                ]}
              >
                <Input.Password id="secretAccessKey" aria-describedby="secret-access-key" autoComplete="off" />
              </Form.Item>
            </div>
            {chained && (
              <div>
                <div className={classes.sectionTitle}>Chaining Parameter:</div>
                <Form.Item
                  label="Chaining Parameter"
                  name="chainingParameter"
                  rules={[
                    {
                      required: true,
                      message: 'Chaining parameter is required',
                    },
                    {
                      whitespace: true,
                      message: 'Cannot contain only whitespace',
                    },
                    {
                      max: 200,
                      message: 'Chaining parameter cannot exceed 200 characters',
                    },
                  ]}
                >
                  <Input
                    id="chainingParameter"
                    aria-describedby="simpleflow-chainingParameter"
                    onChange={handleFieldChange}
                    className={classes.inputMarginTop}
                  />
                </Form.Item>
              </div>
            )}
            {/* FlowChunkInformation - inlined */}
            <div className={classes.marginTop}>
              <div className={classes.sectionTitle}>Chunk Information:</div>
              <Space align="start" size="middle">
                <Form.Item
                  name="chunks"
                  label="Number of Chunks"
                  className={classes.horItem}
                  rules={[
                    {
                      type: 'number',
                      message: 'Must be a number',
                    },
                    {
                      type: 'integer',
                      message: 'Must be a whole number',
                    },
                    {
                      type: 'number',
                      min: 1,
                      message: 'Must be a positive number',
                    },
                  ]}
                >
                  <InputNumber
                    id="chunks"
                    placeholder="Number of Chunks"
                    className={classes.horInput}
                    aria-describedby="project-flow-chunks"
                    onChange={(value) => handleFieldChange({ target: { name: 'chunks', value: value } } as any)}
                  />
                </Form.Item>
                <Form.Item
                  name="chunkCollectHours"
                  label="Chunk Collect Hours"
                  className={classes.horItem}
                  rules={[
                    {
                      type: 'number',
                      message: 'Must be a number',
                    },
                    {
                      type: 'number',
                      min: chunkInputProps.min,
                      message: `Must be at least ${chunkInputProps.min}`,
                    },
                    {
                      type: 'number',
                      max: chunkInputProps.max,
                      message: `Must not exceed ${chunkInputProps.max}`,
                    },
                  ]}
                >
                  <InputNumber
                    id="chunkCollectHours"
                    placeholder="Chunk Collect Hours"
                    className={classes.horInput}
                    aria-describedby="project-flow-chunk-collect"
                    onChange={(value) =>
                      handleFieldChange({
                        target: { name: 'chunkCollectHours', value: value },
                      } as any)
                    }
                    min={chunkInputProps.min}
                    max={chunkInputProps.max}
                    step={chunkInputProps.step}
                  />
                </Form.Item>
                <Form.Item
                  name="chunkPushHours"
                  className={classes.horItem}
                  label="Chunk Push Hours"
                  rules={[
                    {
                      type: 'number',
                      message: 'Must be a number',
                    },
                    {
                      type: 'number',
                      min: chunkInputProps.min,
                      message: `Must be at least ${chunkInputProps.min}`,
                    },
                    {
                      type: 'number',
                      max: chunkInputProps.max,
                      message: `Must not exceed ${chunkInputProps.max}`,
                    },
                  ]}
                >
                  <InputNumber
                    id="chunkPushHours"
                    placeholder="Chunk Push Hours"
                    className={classes.horInput}
                    aria-describedby="project-flow-chunk-push"
                    onChange={(value) => handleFieldChange({ target: { name: 'chunkPushHours', value: value } } as any)}
                    min={chunkInputProps.min}
                    max={chunkInputProps.max}
                    step={chunkInputProps.step}
                  />
                </Form.Item>
                <Form.Item
                  name="chunkTimeoutHours"
                  className={classes.horItem}
                  label="Chunk Timeout Hours"
                  rules={[
                    {
                      type: 'number',
                      message: 'Must be a number',
                    },
                    {
                      type: 'number',
                      min: chunkInputProps.min,
                      message: `Must be at least ${chunkInputProps.min}`,
                    },
                    {
                      type: 'number',
                      max: chunkInputProps.max,
                      message: `Must not exceed ${chunkInputProps.max}`,
                    },
                  ]}
                >
                  <InputNumber
                    id="chunkTimeoutHours"
                    placeholder="Chunk Timeout Hours"
                    className={classes.horInput}
                    onChange={(value) =>
                      handleFieldChange({
                        target: { name: 'chunkTimeoutHours', value: value },
                      } as any)
                    }
                    min={chunkInputProps.min}
                    max={chunkInputProps.max}
                    step={chunkInputProps.step}
                  />
                </Form.Item>
              </Space>
            </div>
            <Divider className={classes.divider} />
            {/* FlowChunkAggregation - inlined */}
            <div>
              <div className={`${classes.switchContainer} ${classes.marginBottom10}`}>
                <Switch
                  checked={flow.aggregateChunks || false}
                  disabled={!flow.chunks}
                  onChange={(checked) =>
                    handleFieldChange({
                      target: { name: 'aggregateChunks', checked: checked, type: 'checkbox' },
                      stopPropagation: () => {},
                      persist: () => {},
                    } as any)
                  }
                />
                <span>Do you want to aggregate the chunks to be pushed for this flow?</span>
              </div>
              <div className={flow.aggregateChunks ? classes.spacingCheckboxes : classes.hidden}>
                <div>
                  <Form.Item name="aggregateChunks" valuePropName="checked" className={classes.marginBottom0}>
                    <Checkbox disabled data-test-id="aggregateChunks-checkbox">
                      Aggregate Chunks
                    </Checkbox>
                  </Form.Item>
                </div>
                <div>
                  <Form.Item name="retainOriginalChunkFiles" valuePropName="checked" className={classes.marginBottom0}>
                    <Checkbox
                      disabled={!flow.chunks}
                      onChange={handleFieldChange}
                      data-test-id="retainOriginalChunkFiles-checkbox"
                    >
                      Retain Original Chunk File Pushes
                    </Checkbox>
                  </Form.Item>
                </div>
                <div>
                  <Form.Item name="ignoreAggregationDupes" valuePropName="checked" className={classes.marginBottom0}>
                    <Checkbox disabled={!flow.chunks} onChange={handleFieldChange} data-test-id="ignoreAggregationDupes-checkbox">
                      Ignore Duplicate Rows
                    </Checkbox>
                  </Form.Item>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Fragment>
  );
}

export default SimpleExtractorFlow;
